import React from 'react';
import { ArrowRight, Zap, Shield, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useScrollToElement } from '../hooks/useScrollToElement';

const Hero: React.FC = () => {
  const scrollToElement = useScrollToElement();

  return (
    <div className="pt-32 pb-20 text-white bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900">
      <div className="container px-4 mx-auto">
        <div className="flex flex-col items-center lg:flex-row">
          {/* Hero Text */}
          <div className="mb-12 lg:w-1/2 lg:mb-0">
            <motion.h1
              className="mb-6 text-4xl font-bold leading-tight md:text-5xl lg:text-6xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Optimize Your PC <span className="text-primary-400">Like Never Before</span>
            </motion.h1>

            <motion.p
              className="mb-8 text-xl text-dark-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              WinMax Optimizer supercharges your Windows PC, eliminating bottlenecks and giving you the speed and performance you deserve.
            </motion.p>

            <motion.div
              className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Link
                to="/product/winmax-custom-duration"
                className="flex justify-center items-center px-8 py-3 font-medium text-white rounded-md transition-colors bg-primary-500 hover:bg-primary-600"
              >
                Get Started
                <ArrowRight size={16} className="ml-2" />
              </Link>
              <button
                onClick={() => scrollToElement('features')}
                className="flex justify-center items-center px-8 py-3 font-medium text-white bg-transparent rounded-md border transition-colors border-dark-600 hover:bg-dark-800"
              >
                Learn More
              </button>
            </motion.div>

            <motion.div
              className="flex flex-wrap gap-6 mt-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <div className="flex items-center">
                <Zap size={18} className="mr-2 text-primary-400" />
                <span className="text-dark-300">Faster Performance</span>
              </div>
              <div className="flex items-center">
                <Shield size={18} className="mr-2 text-primary-400" />
                <span className="text-dark-300">Enhanced Security</span>
              </div>
              <div className="flex items-center">
                <Clock size={18} className="mr-2 text-primary-400" />
                <span className="text-dark-300">Custom Duration</span>
              </div>
            </motion.div>
          </div>

          {/* Hero Image */}
          <motion.div
            className="lg:w-1/2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="relative">
              <img
                src="pexels-photo-577585.webp"
                alt="PC Optimization Dashboard"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-4 -left-4 px-6 py-3 text-white rounded-lg border shadow-lg bg-dark-800 border-dark-700">
                <div className="font-bold">200%+ Performance Boost</div>
                <div className="text-sm text-dark-400">Average improvement</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Hero;